/**
 * Guest Project Selector
 * Lets guests choose the browser-stored project that receives a product.
 */
class GuestProjectSelector {
  constructor() {
    this.currentItem = null;
    this.isCreating = false;
    this.handleOpen = this.handleOpen.bind(this);
    this.handleWishlistChange = this.render.bind(this);
    window.addEventListener('guestProjectSelectionRequest', this.handleOpen);
    window.addEventListener('guestWishlistChange', this.handleWishlistChange);
  }

  handleOpen(event) {
    if (!window.guestWishlistManager || !event.detail?.item) return;

    this.currentItem = event.detail.item;
    this.isCreating = false;
    this.ensureDialog();
    this.render();
    this.dialog.showModal();
  }

  ensureDialog() {
    if (this.dialog) return;

    this.dialog = document.createElement('dialog');
    this.dialog.className = 'guest-project-selector';
    this.dialog.setAttribute('aria-labelledby', 'guest-project-selector-title');
    this.dialog.innerHTML = `
      <div class="guest-project-selector__content">
        <div class="guest-project-selector__header">
          <h2 id="guest-project-selector-title">Add to Project List</h2>
          <button class="guest-project-selector__close" type="button" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="guest-project-selector__body">
          <div class="guest-project-selector__lists" role="list"></div>
        </div>
        <div class="guest-project-selector__footer">
          <div class="guest-project-selector__footer-actions">
            <button class="guest-project-selector__secondary" type="button"></button>
            <button class="guest-project-selector__primary" type="button">Continue</button>
          </div>
        </div>
      </div>
    `;

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.querySelector('.guest-project-selector__close').addEventListener('click', () => this.dialog.close());
    this.dialog.querySelector('.guest-project-selector__secondary').addEventListener('click', () => {
      this.isCreating = !this.isCreating;
      this.render();
    });
    this.dialog.querySelector('.guest-project-selector__primary').addEventListener('click', () => {
      if (!this.isCreating) {
        this.dialog.close();
        return;
      }

      const input = this.dialog.querySelector('[name="project-name"]');
      const name = input?.value.trim();
      if (!name) {
        input?.focus();
        return;
      }

      const project = window.guestWishlistManager.createWishlist(name);
      window.guestWishlistManager.addItemToWishlist(project.id, this.currentItem);
      this.isCreating = false;
      this.notify(`Added to ${project.name}`);
      this.render();
    });
    document.body.appendChild(this.dialog);
  }

  render() {
    if (!this.dialog || !this.currentItem || !window.guestWishlistManager) return;

    const projects = window.guestWishlistManager.getAllWishlists();
    const list = this.dialog.querySelector('.guest-project-selector__lists');
    const title = this.dialog.querySelector('#guest-project-selector-title');
    const secondaryButton = this.dialog.querySelector('.guest-project-selector__secondary');
    const primaryButton = this.dialog.querySelector('.guest-project-selector__primary');
    list.replaceChildren();

    title.textContent = this.isCreating ? 'Create New Project' : 'Add to Project Lists';
    secondaryButton.innerHTML = this.isCreating
      ? 'Cancel'
      : '<svg width="12" height="13" viewBox="0 0 12 13" fill="none" aria-hidden="true"><path d="M1 6.5H11M6 11.5L6 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>Create New Project';
    primaryButton.textContent = this.isCreating ? 'Create Project' : 'Continue';

    if (this.isCreating) {
      const form = document.createElement('div');
      form.className = 'guest-project-selector__input-group';
      form.innerHTML = `
        <label for="guest-project-name"><span>Project name</span></label>
        <div><input id="guest-project-name" name="project-name" type="text" maxlength="80" placeholder="Project name" required></div>
      `;
      list.appendChild(form);
      requestAnimationFrame(() => form.querySelector('input').focus());
      return;
    }

    if (!projects.length) {
      const empty = document.createElement('p');
      empty.className = 'guest-project-selector__empty';
      empty.textContent = 'No project lists found. Create a new project to get started.';
      list.appendChild(empty);
      return;
    }

    projects.forEach((project) => {
      const containsProduct = project.items.some((item) =>
        item.productId == this.currentItem.productId && item.variantId == this.currentItem.variantId
      );
      const row = document.createElement('div');
      row.className = 'guest-project-selector__project';
      row.setAttribute('role', 'listitem');
      row.innerHTML = '<div class="guest-project-selector__project-main"><span class="guest-project-selector__project-name"></span></div><button class="guest-project-selector__project-action" type="button"></button>';
      row.querySelector('.guest-project-selector__project-main').prepend(this.createProjectPreviewGrid(project));
      row.querySelector('.guest-project-selector__project-name').textContent = project.name;
      const action = row.querySelector('.guest-project-selector__project-action');
      action.textContent = containsProduct ? 'Remove' : 'Add';
      action.addEventListener('click', () => this.toggleProject(project, containsProduct));
      list.appendChild(row);
    });
  }

  createProjectPreviewGrid(project) {
    const grid = document.createElement('div');
    grid.className = 'guest-project-selector__project-preview-grid';
    grid.setAttribute('aria-hidden', 'true');

    const previewItems = this.getProjectPreviewItems(project);
    for (let index = 0; index < 4; index += 1) {
      const item = previewItems[index] || null;
      const cell = document.createElement('div');
      cell.className = 'guest-project-selector__project-preview-cell';

      const imageUrl = item?.image || '';
      if (!imageUrl) {
        cell.classList.add('is-empty');
        cell.textContent = '+';
        grid.appendChild(cell);
        continue;
      }

      const image = document.createElement('img');
      image.className = 'guest-project-selector__project-preview-image';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.alt = '';
      image.src = imageUrl;
      image.addEventListener('error', () => {
        cell.replaceChildren();
        cell.classList.add('is-empty');
        cell.textContent = '+';
      }, { once: true });
      cell.appendChild(image);
      grid.appendChild(cell);
    }

    return grid;
  }

  getProjectPreviewItems(project) {
    if (!project || !Array.isArray(project.items)) return [];

    const items = [...project.items].sort((a, b) => {
      const aTime = new Date(a?.addedAt || 0).getTime();
      const bTime = new Date(b?.addedAt || 0).getTime();
      return bTime - aTime;
    });

    return items.slice(0, 4);
  }

  toggleProject(project, containsProduct) {
    if (containsProduct) {
      const item = project.items.find((entry) =>
        entry.productId == this.currentItem.productId && entry.variantId == this.currentItem.variantId
      );
      if (item) window.guestWishlistManager.removeItemFromWishlist(project.id, item.id);
      this.notify(`Removed from ${project.name}`);
      return;
    }

    window.guestWishlistManager.addItemToWishlist(project.id, this.currentItem);
    this.notify(`Added to ${project.name}`);
  }

  notify(message) {
    window.dispatchEvent(new CustomEvent('wishlistNotification', { detail: { message } }));
  }
}

window.guestProjectSelector = new GuestProjectSelector();
